export interface ShallowEqualOptions {
  /**
   * If present, ignore these keys from the shallow comparison
   */
  ignoreKeys?: Set<string>;
  /**
   * If `true`, log a reason as to why the passed values are not shallow equal
   *
   * @default false
   */
  debug?: boolean;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- generic utilities that can accept any type object */
/* eslint-disable no-console -- debug logging */

/**
 * Returns `true` if the passed values are shallow equal.
 * This is different from react-redux's implementation in that
 * it handles maps and sets.
 *
 * What shallow equal means here:
 * - if they are the 'same' (`Object.is` _not_ `===`)
 *   - for objects this means referentially equal
 *   - for strings this means having the same length and same characters
 *   - for primitives this means having the same value
 *   - for all cases see the {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is#description | MDN docs for Object.is}
 * - arrays if they are the same length and every entry is the same
 * - objects if they have the same keys and every value is the same
 * - sets if they are the same length and every entry is the same
 * - maps if they have the same keys and every value is the same
 *   - this utility does _not_ consider key order
 */
export function shallowEqual(
  valA: any,
  valB: any,
  options: ShallowEqualOptions = {},
): boolean {
  const { debug = false } = options;

  if (Object.is(valA, valB)) {
    return true;
  }

  if (
    typeof valA !== "object" ||
    valA == null ||
    typeof valB !== "object" ||
    valB == null
  ) {
    if (debug) {
      console.log(
        "passed values are not the same, not objects, or are nullish",
        valA,
        valB,
      );
    }
    return false;
  }

  if (valA instanceof Set || valB instanceof Set) {
    if (!(valA instanceof Set && valB instanceof Set)) {
      if (debug) {
        console.log("passed values are not both sets", valA, valB);
      }
      return false;
    }

    return setShallowEqual(valA, valB, options);
  }

  if (valA instanceof Map || valB instanceof Map) {
    if (!(valA instanceof Map && valB instanceof Map)) {
      if (debug) {
        console.log("passed values are not both maps", valA, valB);
      }
      return false;
    }

    return mapShallowEqual(valA, valB, options);
  }

  if (Array.isArray(valA) || Array.isArray(valB)) {
    if (!(Array.isArray(valA) && Array.isArray(valB))) {
      if (debug) {
        console.log("passed values are not both arrays", valA, valB);
      }
      return false;
    }

    return arrayShallowEqual(valA, valB, options);
  }

  return objectShallowEqual(valA, valB, options);
}

function setShallowEqual(
  setA: Set<any>,
  setB: Set<any>,
  { debug = false }: ShallowEqualOptions = {},
): boolean {
  if (setA.size !== setB.size) {
    if (debug) {
      console.log("sets have different sizes", setA.size, setB.size);
    }

    return false;
  }

  for (const value of setA.values()) {
    if (!setB.has(value)) {
      if (debug) {
        console.log("both sets did not have this value", value);
      }
      return false;
    }
  }

  return true;
}

function mapShallowEqual(
  mapA: Map<any, any>,
  mapB: Map<any, any>,
  { debug = false, ignoreKeys }: ShallowEqualOptions = {},
): boolean {
  const keysA = omitMapKeys(mapA, ignoreKeys);
  const keysB = omitMapKeys(mapB, ignoreKeys);

  if (keysA.length !== keysB.length) {
    if (debug) {
      console.log("maps do not have the same keys", keysA, keysB);
    }
    return false;
  }

  for (const key of keysA) {
    if (!mapB.has(key)) {
      if (debug) {
        console.log("both maps did not have this key", key);
      }
      return false;
    }

    const valueA = mapA.get(key);
    const valueB = mapB.get(key);

    if (!Object.is(valueA, valueB)) {
      if (debug) {
        console.log(`value for key '${key}' is not the same`, valueA, valueB);
      }
      return false;
    }
  }

  return true;
}

function arrayShallowEqual(
  arrA: any[],
  arrB: any[],
  { debug = false }: ShallowEqualOptions = {},
): boolean {
  if (arrA.length !== arrB.length) {
    if (debug) {
      console.log("arrays have different lengths", arrA.length, arrB.length);
    }

    return false;
  }

  for (let i = 0; i < arrA.length; i++) {
    const valueA = arrA[i];
    const valueB = arrB[i];

    if (!Object.is(valueA, valueB)) {
      if (debug) {
        console.log(`value at index ${i} is not the same`, valueA, valueB);
      }
      return false;
    }
  }

  return true;
}

function objectShallowEqual(
  objA: any,
  objB: any,
  { debug = false, ignoreKeys }: ShallowEqualOptions = {},
): boolean {
  const keysA = omitObjectKeys(objA, ignoreKeys);
  const keysB = omitObjectKeys(objB, ignoreKeys);

  if (keysA.length !== keysB.length) {
    if (debug) {
      console.log("objects do not have the same keys", keysA, keysB);
    }
    return false;
  }

  for (const key of keysA) {
    if (!Object.hasOwn(objB, key)) {
      if (debug) {
        console.log(`objB is missing key '${key}'`, keysB);
      }
      return false;
    }

    if (!Object.is(objA[key], objB[key])) {
      if (debug) {
        console.log(
          `value for key '${key}' is not the same`,
          objA[key],
          objB[key],
        );
      }
      return false;
    }
  }

  return true;
}

/**
 * Returns the keys of a Map, optionally filtering out ignored keys
 */
function omitMapKeys(map: Map<any, any>, ignoreKeys?: Set<string>): any[] {
  const keys = Array.from(map.keys());

  if (ignoreKeys == null) {
    return keys;
  }

  return keys.filter((key) => !ignoreKeys.has(key));
}

/**
 * Version of `Object.keys` that can optionally ignore specific keys
 */
function omitObjectKeys(obj: any, ignoreKeys?: Set<string>): string[] {
  const keys = Object.keys(obj);

  if (ignoreKeys == null) {
    return keys;
  }

  return keys.filter((key) => !ignoreKeys.has(key));
}
